import type { TourId, TourProgress } from './types';

const STORAGE_PREFIX = 'espotz-tours';

const emptyProgress = (): TourProgress => ({
  completed: [],
  skipped: [],
});

const getStorageKey = (userId: string) => `${STORAGE_PREFIX}:${userId}`;

export const readTourProgress = (userId: string | undefined): TourProgress => {
  if (!userId || typeof window === 'undefined') {
    return emptyProgress();
  }

  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) {
      return emptyProgress();
    }

    const parsed = JSON.parse(raw) as Partial<TourProgress>;
    return {
      completed: Array.isArray(parsed.completed) ? parsed.completed : [],
      skipped: Array.isArray(parsed.skipped) ? parsed.skipped : [],
    };
  } catch {
    return emptyProgress();
  }
};

const writeTourProgress = (userId: string, progress: TourProgress) => {
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify(progress));
  } catch {
    // Storage unavailable (private mode) or quota exceeded — tour progress is
    // non-critical, so swallow rather than let it bubble into the Joyride
    // event handler.
  }
};

export const isTourDone = (
  userId: string | undefined,
  tourId: TourId,
): boolean => {
  const progress = readTourProgress(userId);
  return (
    progress.completed.includes(tourId) || progress.skipped.includes(tourId)
  );
};

export const markTourCompleted = (
  userId: string | undefined,
  tourId: TourId,
) => {
  if (!userId) {
    return;
  }

  const progress = readTourProgress(userId);
  if (progress.completed.includes(tourId)) {
    return;
  }

  writeTourProgress(userId, {
    completed: [...progress.completed, tourId],
    skipped: progress.skipped.filter(id => id !== tourId),
  });
};

export const markTourSkipped = (userId: string | undefined, tourId: TourId) => {
  if (!userId) {
    return;
  }

  const progress = readTourProgress(userId);
  if (progress.skipped.includes(tourId)) {
    return;
  }

  writeTourProgress(userId, {
    completed: progress.completed,
    skipped: [...progress.skipped, tourId],
  });
};
